import { EditOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import {
  Alert,
  Button,
  Col,
  Collapse,
  Descriptions,
  Divider,
  Drawer,
  Card as AntdCard,
  Row,
  Space,
  Spin,
  Tag,
  Tooltip,
  message,
} from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import moment from "moment";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import {
  ASSIGN_NOTE_TO_LOAN_REQUEST,
  CREATE_LOAN_REQUEST_KYC,
  CREATE_LOAN_REQUEST_NOTE_TICKET,
  GET_LOAN_REQUEST,
  GET_LOAN_REQUEST_LOGS,
  GET_LOAN_REQUEST_NOTIFIER_RECORDS_QUERY,
  LOAN_KYC_DATA,
  MOVE_LOAN_REQUEST_TO_STATE,
  REGENERATE_LOAN_APPLICATION_FORM,
  UPDATE_LOAN_REQUEST_KYC,
} from "../gql/loans";
import {
  ActiveDrawerContent,
  APIError,
  Attachment,
  Changelog,
  CustomerLien,
  Entitlement,
  FinancialInstitution,
  FormFieldConfig,
  Id,
  LoanRequest,
  MaritalStatus,
  MultipleChangelog,
  Note,
  NotificationRecord,
  SelectOptionsInterface,
  Ticket,
  TransitionToNextState,
  User,
} from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  formatSnakeCase,
  formatter,
  generateDisplayErrorMessage,
  generateNameFromCustomer,
  generateStateButtonText,
  getStateButtonClass,
  getStateColor,
  handleValidationErrors,
  request,
  responseHandler,
} from "../helpers/utilities";
import RequestChangeLogs from "../components/RequestChangeLogs";
import { LoanRequestKycForm } from "../components/LoanRequestKycForm";
import { UploadFile } from "antd/lib/upload/interface";
import { LoanEligibilityCard } from "../components/LoanEligibilityCard";
import { CustomerLienView } from "../components/CustomerLienView";
import { LoanRequestKycView } from "../components/LoanRequestKycView";
import { LoanRequestKycReview } from "../components/LoanRequestKycReview";
import {
  notificationColumns,
  NotificationRecordTableFactory,
} from "./NotificationRecords";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import NotesSection from "../components/NotesSection";
import { CREATE_NOTE } from "../gql/notes";
import { paymentMethods, UNEDITABLE_LOAN_STATES } from "../helpers/constants";

interface State {
  id: string | number;
  isLoading: boolean;
  showReasonModal: boolean;
  isReasonLoading: boolean;
  isRegeneratingForm: boolean;
  selectedTransitionState?: string;
  loanRequest?: LoanRequest;
  notes: Note[];
  currentNote?: Note;
  attachments: Attachment[];
  attachmentIds: string[];
  requestLogs: MultipleChangelog | Changelog[];
  showDrawer: boolean;
  currentRequestsToProcess: number;
  availableTransitionStates?: TransitionToNextState[];
  commentRequired: boolean;
  activeDrawerContent: ActiveDrawerContent;
  showCancelModal: boolean;
  notifications?: NotificationRecord[];
  showKycModal: boolean;
  maritalStatuses: MaritalStatus[];
  idTypes: Id[];
  ghanaCardFrontUrl?: string;
  ghanaCardBackUrl?: string;
  passportUrl?: string;
  applicationFormUrl?: string;
  threeMonthsPayslipUrl?: string;
  standingOrderUrl?: string;
  threeMonthBankStatementUrl?: string;
  threeMomoStatementUrl?: string;
  oneYearConsistentTier2ContributionUrl?: string;
  clearanceFormUrl?: string;
  settlementQuotationUrl?: string;
  showTicketForm: boolean;
  isTicketCreateLoading: boolean;
  selectedLoanNoteId: string;
}

interface RouteParams {
  id: string;
}

interface Props
  extends RouteComponentProps<
    RouteParams,
    any,
    { type: string; requests?: LoanRequest[] }
  > {
  entitlement: Entitlement;
  user: User;
}

export const customerLiensColumns = [
  {
    title: "Provider",
    dataIndex: "provider",
    key: "provider",
    width: "15%",
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Client",
    dataIndex: "company",
    key: "company",
    width: "15%",
    render(text: any, record: CustomerLien): JSX.Element {
      return <span>{record.company?.name || "N/A"}</span>;
    },
  },
  {
    title: "Current Amount",
    dataIndex: "currentAmount",
    key: "currentAmount",
    width: "12%",
    render(text: number): JSX.Element {
      return <span>{formatter.format(text)}</span>;
    },
  },
  {
    title: "Total Amount",
    dataIndex: "totalAmount",
    key: "totalAmount",
    width: "12%",
    render(text: number): JSX.Element {
      return <span>{formatter.format(text)}</span>;
    },
  },
  {
    title: "Status",
    dataIndex: "loanPaid",
    key: "loanPaid",
    width: "10%",
    render(text: boolean, record: CustomerLien): JSX.Element {
      return (
        <Tag color={text ? "green" : "red"} key={record.id}>
          {text ? "Paid" : "Not Paid"}
        </Tag>
      );
    },
  },
  {
    title: "Tax Breakdown",
    key: "taxBreakdown",
    width: "20%",
    render(text: string, record: CustomerLien): JSX.Element {
      return (
        <div style={{ fontSize: "12px" }}>
          <div>
            <strong>Employee:</strong> Pre:{" "}
            {formatter.format(record.employeePreTax)} | Post:{" "}
            {formatter.format(record.employeePostTax)}
          </div>
          <div>
            <strong>Employer:</strong> Pre:{" "}
            {formatter.format(record.employerPreTax)} | Post:{" "}
            {formatter.format(record.employerPostTax)}
          </div>
        </div>
      );
    },
  },
  {
    title: "Period",
    key: "period",
    width: "16%",
    render(text: string, record: CustomerLien): JSX.Element {
      return (
        <div style={{ fontSize: "12px" }}>
          {record.startDate && (
            <div>
              <strong>Start:</strong>{" "}
              {moment(record.startDate, "YYYY-MM-DD").format("DD/MM/YYYY")}
            </div>
          )}
          {record.endDate && (
            <div>
              <strong>End:</strong>{" "}
              {moment(record.endDate, "YYYY-MM-DD").format("DD/MM/YYYY")}
            </div>
          )}
        </div>
      );
    },
  },
];

export const LoanRequestReasonUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  loanRequest?: LoanRequest;
  isLoading?: boolean;
  selectedTransitionState?: string;
  commentRequired?: boolean;
  availableTransitionStates?: TransitionToNextState[];
  handleFileUploadChange?: (field: string, combinedInfo: string) => void;
  financialInstitutions?: FinancialInstitution[];
  handlePaymentMethodSelect?: (paymentMethod: string) => void;
  institutionOptions?: FinancialInstitution[];
  selectedPaymentMethod?: string;
  reasons?: SelectOptionsInterface[];
  handleReasonSelect?: (reason: string) => void;
  requiresIsin?: boolean;
}): JSX.Element => {
  const selectedTransition = props.availableTransitionStates?.find(
    (t) => t.state === props.selectedTransitionState,
  );
  const requiresDisbursement =
    selectedTransition?.complete_disbursement || false;
  const requiresPayment = selectedTransition?.create_payment || false;
  const requiresIssuedDate = selectedTransition?.issued_date_required || false;
  const requiresReason = selectedTransition?.reason_required || false;
  const requiresPaymentInstruction =
    selectedTransition?.create_payment_instruction || false;

  if (requiresPaymentInstruction) {
    return <></>;
  }

  const fields: FormFieldConfig[] = [
    {
      id: "comment",
      name: "comment",
      required: !!props.commentRequired,
      inputType: "textArea",
      rule: "string",
      label: "Add Comment",
      placeholder: "Enter description",
      colConfig: {
        span: 24,
      },
    },
  ];

  if (requiresReason) {
    fields.push({
      id: "fixedIncomeRequestReasonId",
      name: "fixedIncomeRequestReasonId",
      rule: "string",
      inputType: "select",
      required: true,
      label: "Select Reason",
      placeholder: "Select Reason",
      selectHandler: (reason) => props.handleReasonSelect?.(reason),
      selectOptions: props.reasons || [],
      colConfig: { span: 24 },
    });
  }

  if (requiresIssuedDate) {
    fields.push({
      id: "issuedDate",
      name: "issuedDate",
      required: true,
      inputType: "defaultDate",
      rule: "object",
      label: "Issued Date",
      placeholder: "Select issued date",
      elementStyle: { width: "100%" },
      colConfig: { span: 24 },
    });

    if (props.requiresIsin) {
      fields.push({
        id: "isin",
        name: "isin",
        required: true,
        inputType: "string",
        rule: "string",
        label: "ISIN",
        placeholder: "Enter ISIN",
        colConfig: { span: 24 },
      });
    }
  }

  if (requiresPayment) {
    const isMobileMoney = props.selectedPaymentMethod === "mobile_money";
    fields.push(
      {
        id: "paymentAmount",
        name: "paymentAmount",
        required: true,
        inputType: "number",
        rule: "number",
        label: "Payment Amount",
        placeholder: "Enter payment amount",
        colConfig: { span: 24 },
        elementStyle: { width: "100%" },
      },
      {
        id: "paymentDate",
        name: "paymentDate",
        required: true,
        inputType: "defaultDate",
        rule: "object",
        label: "Payment Date",
        placeholder: "Select payment date",
        elementStyle: { width: "100%" },
        colConfig: { span: 24 },
      },
      {
        id: "paymentMethod",
        name: "paymentMethod",
        inputType: "select",
        rule: "string",
        required: false,
        placeholder: "Select payment method",
        label: "Payment Method",
        selectOptions: paymentMethods,
        selectHandler: props.handlePaymentMethodSelect,
        colConfig: { span: 24 },
      },
      {
        id: "bankName",
        name: "bankName",
        required: false,
        inputType: "select",
        rule: "string",
        label: isMobileMoney ? "Mobile Money Provider" : "Bank Name",
        placeholder: isMobileMoney ? "Select provider" : "Select bank",
        selectOptions:
          props.institutionOptions?.map((fi) => ({
            id: fi.id,
            name: fi.name,
            value: fi.name,
            label: fi.name,
          })) || [],
        colConfig: { span: 24 },
      },
      {
        id: "accountName",
        name: "accountName",
        required: false,
        inputType: "string",
        rule: "string",
        label: "Account Name",
        placeholder: "Enter account name",
        colConfig: { span: 24 },
      },
      {
        id: "accountNumber",
        name: "accountNumber",
        required: false,
        inputType: isMobileMoney ? "phone" : "string",
        rule: "string",
        label: isMobileMoney ? "Mobile Money Number" : "Account Number",
        placeholder: isMobileMoney
          ? "Enter mobile money number"
          : "Enter account number",
        colConfig: { span: 24 },
        elementStyle: { width: "100%" },
      },
      {
        id: "paymentReceiptUrl",
        name: "paymentReceiptUrl",
        label: "Payment Receipt",
        inputType: "fileUpload",
        placeholder: "Upload payment receipt",
        rule: "string",
        changeHandler: (combinedInfo: string) =>
          props.handleFileUploadChange?.("paymentReceiptUrl", combinedInfo),
        required: false,
        colConfig: { span: 24 },
      },
    );
  }

  if (requiresDisbursement) {
    fields.push(
      {
        id: "disbursedAmount",
        name: "disbursedAmount",
        required: true,
        inputType: "number",
        rule: "number",
        label: "Disbursed Amount",
        placeholder: "Enter disbursed amount",
        colConfig: {
          span: 24,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        id: "disbursedAt",
        name: "disbursedAt",
        required: true,
        inputType: "defaultDate",
        rule: "object",
        label: "Disbursement Date",
        placeholder: "Select disbursement date",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
    );
  }

  return (
    <>
      <Spin spinning={props.isLoading}>
        <Row>
          <Col
            span={24}
            style={{
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                marginTop: "10px",
              }}
            >
              <BaseForm
                layout="vertical"
                reference={props.reference}
                fieldsToHide={[]}
                fields={fields}
              />
            </div>
          </Col>
        </Row>
      </Spin>
    </>
  );
};

export default class ViewLoanRequest extends React.Component<Props, State> {
  reference: RefObject<FormInstance>;
  reasonReference: RefObject<FormInstance>;
  ticketReference: RefObject<FormInstance>;

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      loanRequest: undefined,
      isLoading: true,
      id: "",
      showReasonModal: false,
      isReasonLoading: false,
      isRegeneratingForm: false,
      notes: [],
      currentNote: undefined,
      attachments: [],
      attachmentIds: [],
      requestLogs: [],
      showDrawer: false,
      currentRequestsToProcess: 0,
      commentRequired: false,
      activeDrawerContent: "",
      showCancelModal: false,
      showKycModal: false,
      maritalStatuses: [],
      idTypes: [],
      ghanaCardFrontUrl: undefined,
      ghanaCardBackUrl: undefined,
      passportUrl: undefined,
      applicationFormUrl: undefined,
      threeMonthsPayslipUrl: undefined,
      standingOrderUrl: undefined,
      threeMonthBankStatementUrl: undefined,
      threeMomoStatementUrl: undefined,
      oneYearConsistentTier2ContributionUrl: undefined,
      clearanceFormUrl: undefined,
      settlementQuotationUrl: undefined,
      showTicketForm: false,
      isTicketCreateLoading: false,
      selectedLoanNoteId: "",
    };
    this.getLoanRequest = this.getLoanRequest.bind(this);
    this.reference = React.createRef<FormInstance>();
    this.reasonReference = React.createRef<FormInstance>();
    this.ticketReference = React.createRef<FormInstance>();
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  setReasonReference = (ref: React.RefObject<FormInstance>): void => {
    this.reasonReference = ref;
  };

  redirectToAllLoanRequestsPage = (): void => {
    this.props.history.push("/loan-requests");
  };

  getLoanRequestLogs = (): void => {
    const { loanRequest } = this.state;
    interface Response {
      loanRequestChangeLogs: MultipleChangelog;
    }

    this.setState({ isLoading: true });
    request({
      query: GET_LOAN_REQUEST_LOGS,
      variables: { loanRequestId: loanRequest?.id },
    })
      .then(({ data }: AxiosResponse<{ data: Response }>): void => {
        this.setState({ requestLogs: data?.data?.loanRequestChangeLogs });
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  toggleKycModal = (): void => {
    this.setState((prevState) => ({
      showKycModal: !prevState.showKycModal,
    }));
  };

  handleKycSubmit = async (kycData: any): Promise<void> => {
    interface Response {
      loanRequestKyc: any;
      errors: APIError[];
    }

    this.setState({ isLoading: true });

    try {
      const { loanRequest } = this.state;
      const isEditMode = !!loanRequest?.loanRequestKyc;

      const mutation = isEditMode
        ? UPDATE_LOAN_REQUEST_KYC
        : CREATE_LOAN_REQUEST_KYC;
      const mutationName = isEditMode
        ? "updateLoanRequestKyc"
        : "createLoanRequestKyc";

      const {
        ghanaCardFrontUrl,
        ghanaCardBackUrl,
        passportUrl,
        applicationFormUrl,
        threeMonthsPayslipUrl,
        standingOrderUrl,
        threeMonthBankStatementUrl,
        threeMomoStatementUrl,
        oneYearConsistentTier2ContributionUrl,
        clearanceFormUrl,
        settlementQuotationUrl,
      } = this.state;

      let variables;
      if (isEditMode) {
        const { loanRequestId, ...updateData } = kycData;
        variables = {
          id: loanRequest?.loanRequestKyc?.id,
          ...updateData,
          ghanaCardFrontUrl,
          ghanaCardBackUrl,
          passportUrl,
          applicationFormUrl,
          threeMonthsPayslipUrl,
          standingOrderUrl,
          threeMonthBankStatementUrl,
          threeMomoStatementUrl,
          oneYearConsistentTier2ContributionUrl,
          clearanceFormUrl,
          settlementQuotationUrl,
        };
      } else {
        variables = {
          ...kycData,
          ghanaCardFrontUrl:
            ghanaCardFrontUrl || loanRequest?.customer?.ghanaCardIdUrl,
          ghanaCardBackUrl:
            ghanaCardBackUrl || loanRequest?.customer?.ghanaCardIdBackUrl,
          passportUrl,
          applicationFormUrl,
          threeMonthsPayslipUrl,
          standingOrderUrl,
          threeMonthBankStatementUrl,
          threeMomoStatementUrl,
          oneYearConsistentTier2ContributionUrl,
          clearanceFormUrl,
          settlementQuotationUrl,
        };
      }

      const response: AxiosResponse = await request({
        query: mutation,
        variables,
      });

      const { err, mutation: mutationResult } = responseHandler<Response>(
        response,
        mutationName,
      );

      if (err || mutationResult.errors?.length) {
        message.error(
          generateDisplayErrorMessage(err || mutationResult.errors),
        );
      } else {
        message.success(
          isEditMode
            ? "KYC updated successfully!"
            : "KYC submitted successfully!",
        );
        this.setState({ showKycModal: false });
        this.getLoanRequest();
      }
    } catch (error) {
      message.error(
        this.state.loanRequest?.loanRequestKyc
          ? "Failed to update KYC"
          : "Failed to submit KYC",
      );
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getLoanRequest(): void {
    const { id } = this.state;
    interface Response {
      getLoanRequest: LoanRequest;
    }
    const query = GET_LOAN_REQUEST;
    this.setState({ isLoading: true });
    request({
      query,
      variables: { id },
    })
      .then(({ data }: AxiosResponse<{ data: Response }>): void => {
        const { getLoanRequest } = data.data;
        this.setState({
          loanRequest: getLoanRequest,
          availableTransitionStates: getLoanRequest?.canTransitionToNextStates,
          notes:
            getLoanRequest?.loanRequestNotes?.map((loanRequestNote) => {
              return {
                ...loanRequestNote.note,
                referenceId: loanRequestNote.id,
              };
            }) || [],
          //   attachments:
          //     loanRequest?.loanRequestAttachments?.map(
          //       (loanRequestAttachment) => loanRequestAttachment.attachment
          //     ) || [],
          //   loanRequestAttachments: loanRequest?.loanRequestAttachments || [],
        });
        this.getLoanRequestLogs();
      })
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  }

  toggleShowReasonModal = (): void => {
    this.setState((previousState: Readonly<State>) => ({
      showReasonModal: !previousState.showReasonModal,
    }));
  };

  saveStateReason = async (): Promise<void> => {
    if (this.state.isReasonLoading) {
      return;
    }

    const { selectedTransitionState, availableTransitionStates } = this.state;

    let data;

    if (!this.reasonReference.current) return;

    const selectedTransition = availableTransitionStates?.find(
      (t) => t.state === selectedTransitionState,
    );
    const requiresDisbursement =
      selectedTransition?.complete_disbursement || false;

    const fieldsToBeValidated = requiresDisbursement
      ? ["comment", "disbursedAmount", "disbursedAt"]
      : ["comment"];

    try {
      data = await this.reasonReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    interface Response {
      loanRequest: LoanRequest;
      errors: APIError[];
    }

    this.setState({ isReasonLoading: true });

    const transition = this.state.availableTransitionStates?.find(
      (t) => t.state === selectedTransitionState,
    );

    const variables: any = {
      loanRequestId: this.state.id,
      comment: data.comment,
      state: selectedTransitionState,
      event: transition?.event,
    };

    if (requiresDisbursement) {
      if (data.disbursedAmount) {
        variables.disbursedAmount = data.disbursedAmount;
      }
      if (data.disbursedAt) {
        variables.disbursedAt = data.disbursedAt;
      }
    }

    try {
      const response: AxiosResponse = await request({
        query: MOVE_LOAN_REQUEST_TO_STATE,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "moveLoanRequestToState",
      );

      const error = err || mutation.errors;

      if (mutation.loanRequest) {
        const newState = mutation.loanRequest.state;
        const stateMessages: Record<string, string> = {
          disbursed: "Loan has been successfully disbursed",
          approved: "Loan request has been approved",
          rejected: "Loan request has been rejected",
          pending: "Loan request moved to pending",
          awaiting_application_forms: "Loan request is awaiting application forms",
          under_review: "Loan request is under review",
          hold: "Loan request moved to hold",
          sent_to_provider: "Loan request has been sent to provider",
        };

        const successMessage = newState
          ? stateMessages[newState] ||
            `Loan request state updated to ${formatSnakeCase(newState)}`
          : "Loan request state updated successfully";

        message.success(successMessage);

        this.getLoanRequest();
      } else {
        message.error(generateDisplayErrorMessage(error));
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.reasonReference.current.resetFields();
      this.setState({
        isReasonLoading: false,
        showReasonModal: false,
      });
    }
  };

  regenerateApplicationForm = async (): Promise<void> => {
    if (this.state.isRegeneratingForm) {
      return;
    }

    interface Response {
      loanRequest: LoanRequest;
      errors: APIError[];
    }

    this.setState({ isRegeneratingForm: true });

    try {
      const response: AxiosResponse = await request({
        query: REGENERATE_LOAN_APPLICATION_FORM,
        variables: { loanRequestId: this.state.id },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "regenerateLoanApplicationForm",
      );

      const error = err || mutation.errors;

      if (mutation.loanRequest) {
        message.success(
          "Application form generation re-queued. The link will appear here once it's ready.",
        );
        this.getLoanRequest();
      } else {
        message.error(generateDisplayErrorMessage(error));
      }
    } catch (error) {
      Sentry.captureException(error);
      message.error("Failed to re-queue application form generation.");
    } finally {
      this.setState({ isRegeneratingForm: false });
    }
  };

  requestForReason = async (state: string): Promise<void> => {
    if (this.state.isReasonLoading) {
      return;
    }

    this.setState({
      showReasonModal: true,
      selectedTransitionState: state,
    });
  };

  getLoanKycData(): void {
    request({
      query: LOAN_KYC_DATA,
    })
      .then(({ data }: AxiosResponse<any>): void => {
        this.setState({
          maritalStatuses: data.data.maritalStatuses || [],
          idTypes: data.data.idTypes || [],
        });
      })
      .catch(() => {});
  }

  componentDidMount(): void {
    this.setState(
      {
        id: this.props.match.params.id,
        currentRequestsToProcess:
          this.props?.location?.state?.requests?.findIndex(
            (item) =>
              item?.id.toString() === this.props.match.params.id.toString(),
          ) ?? 0,
      },
      (): void => {
        this.getLoanRequest();
        this.getLoanKycData();
      },
    );
  }

  handleNoteUpdate = (updatedNotes: Note[]) => {
    this.setState({ notes: updatedNotes });
  };
  assignNoteToLoanRequest = (noteId: string): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignNoteToLoanRequest: {
          loanRequestNote: {
            id: string;
            note: Note;
          };
        };
      };
    }

    return request({
      query: ASSIGN_NOTE_TO_LOAN_REQUEST,
      variables: { noteId: noteId, loanRequestId: this.state.id },
    }).then(({ data: { data } }: AxiosResponse<Response>): void => {
      const newNote = {
        ...data.assignNoteToLoanRequest.loanRequestNote.note,
        referenceId: data.assignNoteToLoanRequest.loanRequestNote.id,
      };

      this.setState({
        isLoading: false,
        currentNote: newNote,
        notes: [...this.state.notes, newNote],
      });
    });
  };

  //   assignAttachmentToLoanRequest = async (
  //     attachmentIds: string[],
  //     loanRequestId: string | number
  //   ): Promise<void> => {
  //     this.setState({ isLoading: true });

  //     interface Response {
  //       data: {
  //         assignAttachmentsToLoanRequest: {
  //           loanRequest: {
  //             loanRequestAttachments: LoanRequestAttachment[];
  //           };
  //         };
  //       };
  //     }

  //     return request({
  //       query: ASSIGN_ATTACHMENT_TO_LOAN_REQUEST,
  //       variables: {
  //         attachmentIds,
  //         loanRequestId,
  //       },
  //     }).then(({ data }: AxiosResponse<Response>): void => {
  //       this.setState({
  //         isLoading: false,
  //         attachmentIds: [],
  //         loanRequestAttachments:
  //           data.data.assignAttachmentsToLoanRequest.loanRequest
  //             .loanRequestAttachments,
  //       });
  //     });
  //   };

  createNote = async (): Promise<void> => {
    const { user } = this.props;

    interface Response {
      loanRequestNote: Note;
      note: Note;
    }

    const fieldsToBeValidated: string[] = ["description"];

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_NOTE,
        variables: {
          description: data.description,
          userId: user.id,
        },
      });
      this.setState({ isLoading: false });

      const { mutation, err } = responseHandler<Response>(
        response,
        "createNote",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      if (mutation) {
        this.assignNoteToLoanRequest(mutation.note.id);
        message.success("Note created successfully");
      }

      this.reference.current &&
        this.reference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ isLoading: false });
    }
  };

  //   createAttachment = async (combinedInfo: string): Promise<void> => {
  //     interface Response {
  //       attachment: Attachment;
  //     }

  //     const fieldsToBeValidated: string[] = ["url"];

  //     this.setState({ isLoading: true });

  //     try {
  //       const [url, name] = combinedInfo.split("@@@");
  //       const response: AxiosResponse = await request({
  //         query: CREATE_ATTACHMENT,
  //         variables: {
  //           url,
  //           name,
  //         },
  //       });
  //       this.setState({ isLoading: false });

  //       const { mutation } = responseHandler<Response>(
  //         response,
  //         "createAttachment"
  //       );
  //       this.setState(
  //         (state) => ({
  //           attachmentIds: [...state.attachmentIds, mutation.attachment.id],
  //           attachments: [...state.attachments, mutation.attachment],
  //         }),
  //         () =>
  //           this.assignAttachmentToLoanRequest(
  //             this.state.attachmentIds,
  //             this.state.id
  //           )
  //       );

  //       this.reference.current &&
  //         this.reference.current.resetFields(fieldsToBeValidated);
  //     } catch (error) {
  //       Sentry.captureException(error);
  //       this.setState({ isLoading: false });
  //     }
  //   };

  //   unassignAttachmentFromLoanRequest = async (id: string): Promise<void> => {
  //     const { loanRequestAttachments } = this.state;

  //     const loanRequestAttachmentToDelete:
  //       | LoanRequestAttachment
  //       | undefined = loanRequestAttachments.find(
  //       (lra: LoanRequestAttachment) => lra.attachment.id === id
  //     );

  //     interface Response {
  //       result: boolean;
  //       errors: APIError[];
  //     }

  //     try {
  //       this.setState({ isLoading: true });
  //       const response: AxiosResponse = await request({
  //         query: UNASSIGN_ATTACHMENT_FROM_LOAN_REQUEST,
  //         variables: { id: loanRequestAttachmentToDelete?.id },
  //       });
  //       const { mutation } = responseHandler<Response>(
  //         response,
  //         "unassignAttachmentFromLoanRequest"
  //       );

  //       if (!mutation.result) {
  //         message.error("Failed to delete loan request attachment");
  //       } else {
  //         const attachments = this.state.attachments.filter(
  //           (attachment) => attachment.id !== id
  //         );
  //         this.setState({
  //           attachments,
  //           isLoading: false,
  //         });
  //         message.success("Deleted Attachment Successfully");
  //       }
  //     } catch (error) {
  //       message.error("An error occurred, please refresh and try again later");
  //     }
  //   };

  toggleCreateTicket = (id: string): void => {
    this.setState((oldState) => ({
      selectedLoanNoteId: id,
      showTicketForm: !oldState.showTicketForm,
      currentNote: this.state.notes.find((note) => note.referenceId === id),
    }));
  };

  setTicketRef = (ref: React.RefObject<FormInstance>): void => {
    this.ticketReference = ref;
  };

  createTicket = async (): Promise<void> => {
    const { selectedLoanNoteId, loanRequest } = this.state;
    const fieldsToBeValidated = [
      "title",
      "description",
      "email",
      "firstName",
      "lastName",
      "source",
      "summary",
      "issue_type",
      "project",
      "mentions",
      "ticketSubCategoryId",
      "type",
      "assigneeId",
    ];

    interface Response {
      ticket: Ticket;
    }

    if (!this.ticketReference.current) return;

    let data;

    try {
      data = await this.ticketReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isTicketCreateLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_LOAN_REQUEST_NOTE_TICKET,
        variables: {
          loanRequestNoteId: selectedLoanNoteId,
          properties: {
            ...data,
            first_name: data.firstName,
            last_name: data.lastName,
            system_user: this.props.user.email,
            title: `Loan Request - ${loanRequest?.customer?.firstName} ${loanRequest?.customer?.lastName}`,
            group: "Users",
            priority: "2 normal",
            state: "open",
            petra_id: loanRequest?.customer?.petraId,
            assigneeId: data.assigneeId,
          },
          source: data.source,
          ticketSubCategoryId: data.ticketSubCategoryId,
        },
      });

      const { mutation, err } = responseHandler<Response>(
        response,
        "createLoanRequestNoteTicket",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        const currentNote = this.state.currentNote?.tickets
          ? ({
              ...this.state.currentNote,
              tickets: [this.state.currentNote?.tickets, mutation.ticket],
            } as unknown as Note)
          : ({
              ...this.state.currentNote,
              tickets: [mutation.ticket],
            } as unknown as Note);

        this.getLoanRequest();
        this.ticketReference.current.resetFields(fieldsToBeValidated);
        this.setState((oldState) => ({
          showTicketForm: !oldState.showTicketForm,
          notes: [...this.state.notes, currentNote],
        }));
        message.success("Ticket Created Successfully");
      }
    } catch (error) {
      message.error(`Could Not Create Ticket - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
    this.setState({ isTicketCreateLoading: false });
  };

  getRequestNotifierRecords = (): void => {
    const { id: loanRequestId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_LOAN_REQUEST_NOTIFIER_RECORDS_QUERY,
      variables: { loanRequestId },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            getLoanRequest: LoanRequest;
          };
        }>): void => {
          this.setState({
            loanRequest: {
              ...this.state.loanRequest,
              ...data.data.getLoanRequest,
            },
          });
        },
      )
      .finally((): void => {
        this.setState({
          isLoading: false,
        });
      });
  };

  executeDrawerQuery(currentDrawer: ActiveDrawerContent): void {
    switch (currentDrawer) {
      case "notifierRecords":
        this.getRequestNotifierRecords();
        break;
      case "audits":
        this.getLoanRequestLogs();
        break;
      default:
        break;
    }
    return;
  }

  toggleDrawer = (activeDrawerContent: ActiveDrawerContent): (() => void) => {
    return (): void => {
      this.executeDrawerQuery(activeDrawerContent);
      this.setState({
        activeDrawerContent,
        showDrawer: !this.state.showDrawer,
        showCancelModal: false,
      });
    };
  };

  getDrawerContent = (): JSX.Element => {
    const { requestLogs, activeDrawerContent } = this.state;

    switch (activeDrawerContent) {
      case "audits":
        return <RequestChangeLogs data={requestLogs} />;
      case "notifierRecords":
        const records = this.state.loanRequest
          ?.notifierRecords as NotificationRecord[];

        return (
          <Fragment>
            {NotificationRecordTableFactory(records, notificationColumns)}
          </Fragment>
        );
      default:
        return <Fragment />;
    }
  };

  getTitleBasedOnDrawerContent = (): React.ReactNode => {
    const { activeDrawerContent } = this.state;
    switch (activeDrawerContent) {
      case "audits":
        return "Loan Request Logs";
      case "notifierRecords":
        return "Notification Records";
      default:
        return "Entity";
    }
  };

  toggleModal = (): void => {
    this.setState({
      showCancelModal: !this.state.showCancelModal,
    });
  };

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url,
    }));
  };

  handleDelete = (file: UploadFile): void => {
    let newState = {};

    switch (file.name) {
      case "Ghana Card (Front)":
        newState = {
          ghanaCardIdUrl: "",
          ghanaCardFrontUrl: "",
        };
        break;
      case "Ghana Card (Back)":
        newState = { ghanaCardIdBackUrl: "", ghanaCardBackUrl: "" };
        break;
      case "Customer Signature":
        newState = { signatureUrl: "" };
        break;

      case "Passport":
        newState = { passportUrl: "" };
        break;
      case "Application Form":
        newState = { applicationFormUrl: "" };
        break;
      case "3 Months Payslip":
        newState = { threeMonthsPayslipUrl: "" };
        break;
      case "Standing Order":
        newState = { standingOrderUrl: "" };
        break;
      case "3 Month Bank Statement":
        newState = { threeMonthBankStatementUrl: "" };
        break;
      case "3 Month MoMo Statement":
        newState = { threeMomoStatementUrl: "" };
        break;
      case "1 Year Tier 2 Contribution":
        newState = { oneYearConsistentTier2ContributionUrl: "" };
        break;
      case "Clearance Form":
        newState = { clearanceFormUrl: "" };
        break;
      case "Settlement Quotation":
        newState = { settlementQuotationUrl: "" };
        break;
      case "6 Months Bank Statement":
        newState = { sixMonthsBankStatementUrl: "" };
        break;
      case "6 Months Payslips":
        newState = { sixMonthsPayslipsUrl: "" };
        break;
      case "Employment Offer Letter":
        newState = { employmentOfferLetterUrl: "" };
        break;

      default:
        newState = { photoIdUrl: "" };
    }

    this.setState(newState);
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      id,
      loanRequest,
      currentRequestsToProcess,
      showReasonModal,
      selectedTransitionState,
      isReasonLoading,
      isRegeneratingForm,
      showKycModal,
      availableTransitionStates,
      commentRequired,
      showDrawer,
      maritalStatuses,
      idTypes,
      notes,
      showTicketForm,
      isTicketCreateLoading,
      currentNote,
    } = this.state;

    const moveToNextLink = `/loan-requests/${
      this.props.location?.state?.requests?.[currentRequestsToProcess + 1]?.id
    }`;

    const moveToPreviousLink = `/loan-requests/${
      this.props.location?.state?.requests?.[currentRequestsToProcess - 1]?.id
    }`;

    const nextPreviousUI = (
      <>
        <br />
        <Row
          gutter={10}
          style={{
            float: "right",
            marginTop: "10px",
          }}
        >
          <Col>
            {currentRequestsToProcess > 0 && (
              <NavLink
                to={{
                  pathname: moveToPreviousLink,
                  state: {
                    type: "loan",
                    requests: this.props?.location?.state?.requests ?? [],
                  },
                }}
              >
                <Tooltip placement="left" title={"Previous"}>
                  <LeftOutlined style={{ color: "#1D3764" }} />
                </Tooltip>
              </NavLink>
            )}
          </Col>

          <Col>
            {this.props?.location?.state?.requests &&
              currentRequestsToProcess &&
              this.props?.location?.state?.requests?.length - 1 && (
                <NavLink
                  to={{
                    pathname: moveToNextLink,
                    state: {
                      type: "loan",
                      requests: this.props?.location?.state?.requests ?? [],
                    },
                  }}
                >
                  <Tooltip placement="right" title={"Next"}>
                    <RightOutlined style={{ color: "#1D3764" }} />
                  </Tooltip>
                </NavLink>
              )}
          </Col>

          <Col>
            {this.props?.location?.state?.requests &&
              `${currentRequestsToProcess + 1} / ${
                this.props?.location?.state?.requests?.length
              }`}
          </Col>
        </Row>
      </>
    );

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            {ModalCreator({
              title: "Add Comment",
              okType: "primary",
              okText: "Submit",
              showModal: showReasonModal,
              onOkay: this.saveStateReason,
              toggleModal: this.toggleShowReasonModal,
              isLoading: isReasonLoading,
              okayButtonProps: {
                disabled: isReasonLoading,
              },
              inscription: LoanRequestReasonUI({
                reference: this.reasonReference,
                loanRequest: loanRequest,
                selectedTransitionState,
                availableTransitionStates,
                isLoading: isReasonLoading,
                commentRequired,
              }),
            })}
            <Row>
              <Col span={24}>
                {loanRequest && (
                  <>
                    <Descriptions
                      title={<div>Details</div>}
                      extra={
                        <Row
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          {loanRequest?.state === "pending_kyc" &&
                            !loanRequest?.loanRequestKyc && (
                              <Button
                                type="primary"
                                onClick={this.toggleKycModal}
                              >
                                {loanRequest.customer.lastLoanRequestKyc
                                  ? "Review KYC"
                                  : "Upload KYC"}
                              </Button>
                            )}
                          <Button
                            className="base-button"
                            onClick={this.toggleDrawer("notifierRecords")}
                          >
                            Notifications
                          </Button>
                          <Button
                            className="base-button"
                            onClick={this.toggleDrawer("audits")}
                          >
                            Request History
                          </Button>

                          {loanRequest.state &&
                            !UNEDITABLE_LOAN_STATES.includes(loanRequest.state) && (
                              <EntitledSection
                                entitlement={entitlements.LoanRequests.edit}
                              >
                                <NavLink to={`/loan-requests/${id}/edit`}>
                                  <Button
                                    icon={<EditOutlined />}
                                    className="base-button"
                                  >
                                    Edit
                                  </Button>{" "}
                                </NavLink>
                              </EntitledSection>
                            )}
                          {nextPreviousUI}
                        </Row>
                      }
                    ></Descriptions>

                    <div className="loan-request-details-stack">
                      <AntdCard title="Customer Information">
                        <Descriptions>
                          <Descriptions.Item
                            label={<b>Customer Name</b>}
                            key="Customer"
                          >
                            {loanRequest.customer ? (
                              <NavLink
                                to={`/customers/${loanRequest.customer.id}`}
                              >
                                {generateNameFromCustomer(loanRequest.customer)}
                              </NavLink>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Company Name</b>}
                            key="Company"
                          >
                            {loanRequest.company ? (
                              <NavLink
                                to={`/companies/${loanRequest.company.id}`}
                              >
                                {loanRequest.company.name}
                              </NavLink>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Petra ID</b>}
                            key="CustomerPetraId"
                          >
                            {loanRequest.customer?.petraId || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Sales Person</b>}
                            key="salesPerson"
                          >
                            {loanRequest.salesPerson?.name || "N/A"}
                          </Descriptions.Item>
                        </Descriptions>
                      </AntdCard>
                      <AntdCard
                        title={
                          <span>
                            Loan Request{" "}
                            {loanRequest.state ? (
                              <Tag
                                color={getStateColor(loanRequest.state)}
                                style={{
                                  textTransform: "capitalize",
                                  marginLeft: "3px",
                                }}
                              >
                                {formatSnakeCase(loanRequest.state)}
                              </Tag>
                            ) : (
                              "N/A"
                            )}
                          </span>
                        }
                      >
                        <Descriptions>
                          <Descriptions.Item label={<b>Source</b>} key="source">
                            {loanRequest.source
                              ? formatSnakeCase(
                                  capitalizeFirstLetter(loanRequest.source),
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Loan Type</b>}
                            key="LoanType"
                          >
                            {loanRequest.loanType
                              ? formatSnakeCase(
                                  capitalizeFirstLetter(loanRequest.loanType),
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Security Type</b>}
                            key="SecurityType"
                          >
                            {loanRequest.loanSecurityType
                              ? formatSnakeCase(
                                  capitalizeFirstLetter(
                                    loanRequest.loanSecurityType,
                                  ),
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Amount Requested</b>}
                            key="AmountRequested"
                          >
                            {loanRequest.loanAmountRequested !== null &&
                            loanRequest.loanAmountRequested !== undefined
                              ? formatter.format(
                                  loanRequest.loanAmountRequested,
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Tenure (Months)</b>}
                            key="Tenure"
                          >
                            {loanRequest.loanTenureMonths !== null &&
                            loanRequest.loanTenureMonths !== undefined
                              ? loanRequest.loanTenureMonths
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Purpose</b>}
                            key="Purpose"
                          >
                            {loanRequest.loanPurpose || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Max Loan Amount</b>}
                            key="MaxLoanAmount"
                          >
                            {loanRequest.maxLoanAmount !== null &&
                            loanRequest.maxLoanAmount !== undefined
                              ? formatter.format(loanRequest.maxLoanAmount)
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Disbursed Amount</b>}
                            key="DisbursedAmount"
                          >
                            {loanRequest.disbursedAmount !== null &&
                            loanRequest.disbursedAmount !== undefined
                              ? formatter.format(loanRequest.disbursedAmount)
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Disbursement Date</b>}
                            key="DisbursedAt"
                          >
                            {loanRequest.disbursedAt
                              ? moment(
                                  loanRequest.disbursedAt,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("llll")
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Loan Duration</b>}
                            key="LoanDuration"
                          >
                            {loanRequest.loanDuration !== null &&
                            loanRequest.loanDuration !== undefined
                              ? `${loanRequest.loanDuration} months`
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Request Date</b>}
                            key="createdAt"
                          >
                            {loanRequest.createdAt
                              ? moment(
                                  loanRequest.createdAt,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("llll")
                              : "N/A"}
                          </Descriptions.Item>
                        </Descriptions>
                      </AntdCard>
                      <AntdCard title="Product Details">
                        <Descriptions>
                          <Descriptions.Item
                            label={<b>Product</b>}
                            key="Product"
                          >
                            {loanRequest.product?.name || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Provider</b>}
                            key="Provider"
                          >
                            {loanRequest.provider ? (
                              <NavLink
                                to={`/providers/${loanRequest.provider.id}`}
                              >
                                {loanRequest.provider.name}
                              </NavLink>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Portion of Contribution</b>}
                            key="PortionOfContribution"
                          >
                            {loanRequest.portionOfContributionToBeUsed &&
                            loanRequest.portionOfContributionToBeUsed.length >
                              0 ? (
                              <Space size={[0, 8]} wrap>
                                {loanRequest.portionOfContributionToBeUsed.map(
                                  (portion, index) => (
                                    <Tag key={index}>
                                      {formatSnakeCase(
                                        capitalizeFirstLetter(portion),
                                      )}
                                    </Tag>
                                  ),
                                )}
                              </Space>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>
                        </Descriptions>
                      </AntdCard>
                    </div>
                  </>
                )}
              </Col>
            </Row>

            {showKycModal &&
              ModalCreator({
                title: loanRequest?.customer?.lastLoanRequestKyc
                  ? "Review Previous KYC Information"
                  : "Loan Request KYC",
                showModal: showKycModal,
                toggleModal: this.toggleKycModal,
                onOkay: () => {},
                width: 1400,
                showFooter: false,
                inscription:
                  loanRequest?.customer?.lastLoanRequestKyc &&
                  !loanRequest?.loanRequestKyc ? (
                    <LoanRequestKycReview
                      lastKyc={loanRequest.customer.lastLoanRequestKyc as any}
                      loanRequest={loanRequest}
                      customer={loanRequest.customer}
                      maritalStatuses={maritalStatuses}
                      idTypes={idTypes}
                      onConfirmReview={this.handleKycSubmit}
                      onCancel={this.toggleKycModal}
                      isLoading={isLoading}
                      handleFileUploadChange={this.handleFileUploadChange}
                      handleDelete={this.handleDelete}
                    />
                  ) : (
                    <LoanRequestKycForm
                      loanRequest={loanRequest}
                      maritalStatuses={maritalStatuses}
                      idTypes={idTypes}
                      onSubmit={this.handleKycSubmit}
                      onCancel={this.toggleKycModal}
                      isLoading={isLoading}
                      customer={loanRequest?.customer}
                      isEditMode={!!loanRequest?.loanRequestKyc}
                      handleFileUploadChange={this.handleFileUploadChange}
                      handleDelete={this.handleDelete}
                    />
                  ),
                okType: "primary",
                maskClosable: false,
              })}
            <div
              style={{
                marginTop: "25px",
                display: "flex",
                justifyContent: "start",
              }}
            >
              {availableTransitionStates?.map((transition, i) => (
                <Button
                  onClick={() => this.requestForReason(transition.state)}
                  className={getStateButtonClass(transition.state)}
                  id={`transition-btn-${i}`}
                  key={i}
                  style={{ marginRight: "5px" }}
                  disabled={isReasonLoading}
                >
                  {generateStateButtonText(transition.event)}
                </Button>
              ))}
            </div>
          </Fragment>
        </Card>

        {loanRequest?.filledApplicationFormUrl ? (
          <Alert
            message="Filled Application Form"
            description={
              <span>
                The provider&apos;s application form was auto-generated for this
                loan.{" "}
                <a
                  href={loanRequest.filledApplicationFormUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Filled Application Form
                </a>
              </span>
            }
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
        ) : (
          <>
            {loanRequest?.filledApplicationFormStatus === "generating" && (
              <Alert
                message="Application Form Generating"
                description={
                  <span>
                    The application form is being generated. This can take a
                    moment — refresh to check again.
                  </span>
                }
                action={
                  <Button size="small" onClick={this.getLoanRequest}>
                    Refresh
                  </Button>
                }
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}

            {loanRequest?.filledApplicationFormStatus === "failed" && (
              <Alert
                message="Application Form Generation Failed"
                description={
                  <span>
                    {loanRequest.filledApplicationFormFailureReason ||
                      "The last attempt to generate the application form failed."}{" "}
                    You can retry the generation.
                  </span>
                }
                action={
                  <Button
                    size="small"
                    type="primary"
                    loading={isRegeneratingForm}
                    onClick={this.regenerateApplicationForm}
                  >
                    Retry Generation
                  </Button>
                }
                type="error"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}

            {loanRequest?.filledApplicationFormStatus === "pending" &&
              loanRequest?.source === "achieve" &&
              [
                "under_review",
                "pending_ops_processing",
                "pending_ops_approval",
                "hold",
                "approved",
                "sent_to_provider",
                "disbursed",
              ].includes(loanRequest?.state ?? "") && (
                <Alert
                  message="Application Form Pending"
                  description={
                    <span>
                      The application form can now be generated for this loan.
                    </span>
                  }
                  action={
                    <Button
                      size="small"
                      type="primary"
                      loading={isRegeneratingForm}
                      onClick={this.regenerateApplicationForm}
                    >
                      Generate Form
                    </Button>
                  }
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
          </>
        )}

        {loanRequest?.state === "awaiting_application_forms" &&
          loanRequest?.filledApplicationFormUrl && (
            <Alert
              message="Awaiting Signed Application Form"
              description="The filled form has been generated. The customer must upload a signed version before this loan can move to review."
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

        {loanRequest?.loanBreakdown?.error && (
          <Alert
            message="Loan Breakdown Error"
            description={loanRequest?.loanBreakdown?.error}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}

        {loanRequest?.loanBreakdown && !loanRequest?.loanBreakdown?.error && (
          <div style={{ margin: "20px 0" }}>
            <LoanEligibilityCard
              loanBreakdownData={loanRequest.loanBreakdown}
            />
          </div>
        )}

        {loanRequest && (
          <Collapse style={{ marginTop: 16 }}>
            <Collapse.Panel header="Supporting Documents" key="documents">
              <Descriptions column={2} bordered size="small">
                {loanRequest.ghanaCardFrontUrl && (
                  <Descriptions.Item label="Ghana Card (Front)">
                    <a
                      href={loanRequest.ghanaCardFrontUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.ghanaCardBackUrl && (
                  <Descriptions.Item label="Ghana Card (Back)">
                    <a
                      href={loanRequest.ghanaCardBackUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.passportUrl && (
                  <Descriptions.Item label="Passport Picture">
                    <a
                      href={loanRequest.passportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.applicationFormUrl && (
                  <Descriptions.Item label="Application Form">
                    <a
                      href={loanRequest.applicationFormUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.threeMonthsPayslipUrl && (
                  <Descriptions.Item label="3 Months Payslip">
                    <a
                      href={loanRequest.threeMonthsPayslipUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.standingOrderUrl && (
                  <Descriptions.Item label="Standing Order">
                    <a
                      href={loanRequest.standingOrderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.threeMonthBankStatementUrl && (
                  <Descriptions.Item label="3 Month Bank Statement">
                    <a
                      href={loanRequest.threeMonthBankStatementUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.threeMomoStatementUrl && (
                  <Descriptions.Item label="3 Month MoMo Statement">
                    <a
                      href={loanRequest.threeMomoStatementUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.oneYearConsistentTier2ContributionUrl && (
                  <Descriptions.Item label="1 Year Tier 2 Contribution">
                    <a
                      href={loanRequest.oneYearConsistentTier2ContributionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.clearanceFormUrl && (
                  <Descriptions.Item label="Clearance Form">
                    <a
                      href={loanRequest.clearanceFormUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.settlementQuotationUrl && (
                  <Descriptions.Item label="Settlement Quotation">
                    <a
                      href={loanRequest.settlementQuotationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.sixMonthsBankStatementUrl && (
                  <Descriptions.Item label="6 Months Bank Statement">
                    <a
                      href={loanRequest.sixMonthsBankStatementUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.sixMonthsPayslipsUrl && (
                  <Descriptions.Item label="6 Months Payslips">
                    <a
                      href={loanRequest.sixMonthsPayslipsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.employmentOfferLetterUrl && (
                  <Descriptions.Item label="Employment Offer Letter">
                    <a
                      href={loanRequest.employmentOfferLetterUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.borrowersMandateUrl && (
                  <Descriptions.Item label="Borrower's Mandate">
                    <a
                      href={loanRequest.borrowersMandateUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
                {loanRequest.guarantorFormsUrl && (
                  <Descriptions.Item label="Guarantor Forms">
                    <a
                      href={loanRequest.guarantorFormsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </Descriptions.Item>
                )}
              </Descriptions>
              {!loanRequest.ghanaCardFrontUrl &&
                !loanRequest.ghanaCardBackUrl &&
                !loanRequest.passportUrl &&
                !loanRequest.applicationFormUrl &&
                !loanRequest.threeMonthsPayslipUrl &&
                !loanRequest.standingOrderUrl &&
                !loanRequest.threeMonthBankStatementUrl &&
                !loanRequest.threeMomoStatementUrl &&
                !loanRequest.oneYearConsistentTier2ContributionUrl &&
                !loanRequest.clearanceFormUrl &&
                !loanRequest.settlementQuotationUrl &&
                !loanRequest.sixMonthsBankStatementUrl &&
                !loanRequest.sixMonthsPayslipsUrl &&
                !loanRequest.employmentOfferLetterUrl &&
                !loanRequest.borrowersMandateUrl &&
                !loanRequest.guarantorFormsUrl && (
                  <p style={{ color: "#999", fontStyle: "italic" }}>
                    No documents uploaded
                  </p>
                )}
            </Collapse.Panel>
          </Collapse>
        )}

        {loanRequest?.customerLien && (
          <CustomerLienView customerLien={loanRequest.customerLien} />
        )}

        {loanRequest?.loanRequestKyc && loanRequest?.state && (
          <LoanRequestKycView
            loanRequestKyc={loanRequest.loanRequestKyc}
            loanRequestState={loanRequest.state}
            onEdit={this.toggleKycModal}
          />
        )}

        {/* <AttachmentsSection
          reference={this.reference}
          createAttachment={this.createAttachment}
          attachments={attachments}
          showForm={true}
          handleRemove={this.unassignAttachmentFromLoanRequest}
        />
        */}

        <Divider className="view-company-ant-divider" />

        {loanRequest && (
          <Collapse style={{ marginTop: 16 }}>
            <Collapse.Panel header="Notes" key="notes">
              <NotesSection
                showHeading={false}
                reference={this.reference}
                createNote={this.createNote}
                notes={notes}
                showForm={true}
                canCreateTicket={true}
                showTicketForm={showTicketForm}
                toggleTicketCreate={this.toggleCreateTicket}
                createTicket={this.createTicket}
                ticketLoading={isTicketCreateLoading}
                ticketReference={this.ticketReference}
                customer={loanRequest.customer}
                currentNote={currentNote}
                entity="LoanRequest"
                onNoteUpdate={this.handleNoteUpdate}
              />
            </Collapse.Panel>
          </Collapse>
        )}
        <Divider className="view-company-ant-divider" />

        <Drawer
          title={this.getTitleBasedOnDrawerContent()}
          width={1200}
          onClose={this.toggleDrawer("")}
          visible={showDrawer}
          bodyStyle={{ paddingBottom: 80 }}
          maskClosable={false}
        >
          {this.getDrawerContent()}
        </Drawer>
        {/* {this.state.showCancelModal &&
          ModalCreator({
            title: "Cancel",
            okType: "primary",
            okText: "Yes",
            width: 400,
            showModal: true,
            onOkay: this.toggleDrawer(""),
            toggleModal: this.toggleModal,
            showFooter: true,
            inscription: `Are you sure you want to cancel ${spaceCamel(
              this.state.activeDrawerContent
            ).toLocaleLowerCase()} ?`,
          })} */}
      </Spin>
    );
  }
}
